import { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { getOrdersSummary, transferOrders } from '../apis/bopApi';
import { trackAnonymousOrdersFetchForTransferError } from '../apis/newRelicTrackingApi';
import {
  GET_ORDER_SUMMARY,
  GET_SHOPPER_ORDER_DETAILS,
} from '../constants/reactQueryKeys';
import { API_STATUS } from '../constants/apiStatus';
import useIdentityContext from './useIdentityContext';
import useSiteData from './useSiteData';

/**
 * Hook that returns the total number of orders placed  in the same anonymous session as the given order,
 * a function for evoking the transfer of orders to the signed-in shopper, the status of the transfer, and the number of orders transferred.
 *
 * @param {object} order - An order that was placed anonymously by the signed-in shopper
 * @param {boolean} disabled - A flag that disables the hook for orders that are not eligible for transfer
 */
const useAnonymousOrderTransfer = ({ order, disabled }) => {
  const queryClient = useQueryClient();

  const { vistacartTenant, tenantEnvironment } = useSiteData();
  const { orderNumber } = order;
  const { identity } = useIdentityContext();
  const { shopperId } = identity;

  const [numberOfAnonOrders, setNumberOfGuestOrders] = useState(0);
  const [numberOfAnonOrdersTransferred, setNumberOfGuestOrdersTransferred] =
    useState(0);
  const [orderTransferStatus, setOrderTransferStatus] = useState(
    API_STATUS.IDLE
  ); // IDLE, PENDING, SUCCESS, or FAILED

  // Fetch the total number of guest orders they placed in the same session as this order
  useQuery(
    [GET_ORDER_SUMMARY, shopperId],
    () =>
      getOrdersSummary({
        environment: tenantEnvironment,
        offset: 0,
        limit: 1,
        vistacartTenantName: vistacartTenant,
        shopperId: order.shopperId,
      }),
    {
      onSuccess: orders => {
        setNumberOfGuestOrders(orders.total);
      },
      onError: error => {
        setNumberOfGuestOrders(0);
        const metricsData = {
          shopperId,
          anonymousShopperId: order.shopperId,
          validationOrderNumber: orderNumber,
        };
        trackAnonymousOrdersFetchForTransferError(error, metricsData);
      },
      enabled: !disabled,
      keepPreviousData: true,
    }
  );

  // Calls BOP to evoke the transfer of all of the anonymously placed orders and corresponding assets
  // to the signed-in shopper's account
  const transferAnonymousOrdersToSignedInShopper = async () => {
    setOrderTransferStatus(API_STATUS.PENDING);

    let result;
    try {
      result = await transferOrders({
        orderNumber,
        shopperId,
        fromShopperId: order.shopperId,
        environment: tenantEnvironment,
      });

      // On successful transfer, refetch the order to pick up the owner change
      if (result.orderTransferSuccess) {
        queryClient.invalidateQueries(GET_SHOPPER_ORDER_DETAILS);
      }
    } catch (err) {
      console.error(err);
    }

    setOrderTransferStatus(
      result?.orderTransferSuccess ? API_STATUS.SUCCESS : API_STATUS.FAILED
    );
    setNumberOfGuestOrdersTransferred(result?.numberOfOrdersTransferred || 0);
  };

  return {
    numberOfAnonOrders,
    numberOfAnonOrdersTransferred,
    orderTransferStatus,
    transferAnonymousOrdersToSignedInShopper,
  };
};

export default useAnonymousOrderTransfer;
